// DETERMINISTIC answer matching — the fast path of the pending-question protocol. Given the user's typed
// reply and the options Otto offered, decide what the reply MEANS without a model round-trip:
//
//   { type: "none" }               — "none / cancel / never mind": drop the question, nothing runs
//   { type: "pick", picks, all? }  — the selected option(s): quantifiers ("all of them"), an affirmation on
//                                    a one-option question ("yes"), ordinals ("the first one", "1 and 3"),
//                                    or option NAMES (fuzzy, tiered like every other Otto grounding)
//   null                           — not recognizably an answer: the message falls through to the normal
//                                    pipeline (it may be a new request — "actually, filter by HVAC")
//
// Deliberately CONSERVATIVE: a qualified quantifier ("all the florida ones") or an ambiguous name is NOT a
// match — wrong-guessing an answer runs an action the user didn't pick, while falling through only costs
// one round-trip. The semantic long tail ("the east one", "whichever is bigger") is the planned LLM
// answer-resolver's job (slice 3), which slots in exactly where this returns null.

// Normalize like the rest of Otto's grounding: lowercase, strip punctuation, collapse whitespace. (Local
// copy — this module is cross-capability infrastructure and imports no capability.)
const norm = s =>
    String(s == null ? "" : s)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

// Whole-utterance phrases only (never substrings — "no more than 5" must not read as a dismissal).
const NONE_PHRASES = new Set([
    "none", "none of them", "none of those", "none of these", "neither", "neither of them",
    "cancel", "never mind", "nevermind", "forget it", "forget about it",
    "no", "nah", "nope", "no thanks", "no thank you", "skip", "skip it", "stop", "leave it"
]);

const YES_PHRASES = new Set([
    "yes", "yeah", "yep", "sure", "ok", "okay", "sounds good", "go ahead", "do it", "please", "yes please",
    "correct", "right", "that one", "that works", "fine", "alright", "all right", "go for it"
]);

const ALL_PHRASES = new Set(["all", "all of them", "all of those", "all of these", "everything", "every one", "every one of them", "all of the above", "all the above"]);
const COUNT_WORDS = { two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };

// "all", "all of them", "both" (2 options), "all three"/"all 3" (count must MATCH — "all three" against
// five options is not an answer). Only meaningful on a multi-pick question.
const isAllPhrase = (t, count) => {
    if (ALL_PHRASES.has(t)) return true;
    if (t === "both" && count === 2) return true;
    const m = t.match(/^all (\w+)$/);
    if (m) {
        const n = COUNT_WORDS[m[1]] || parseInt(m[1], 10);
        return Number.isFinite(n) && n === count;
    }
    return false;
};

const ORDINAL_WORDS = { first: 0, second: 1, third: 2, fourth: 3, fifth: 4, sixth: 5, seventh: 6, eighth: 7 };

// One fragment as an ORDINAL: "the first (one)", "2nd", "option 3", "number 2", "last". Returns the
// 0-based index or null. Out-of-range is null (not an answer), never a clamp.
const parseOrdinal = (frag, count) => {
    const m = frag.match(/^(?:the )?(?:option |number |no )?(last|first|second|third|fourth|fifth|sixth|seventh|eighth|\d+)(?:st|nd|rd|th)?(?: (?:one|option))?$/);
    if (!m) return null;
    const word = m[1];
    let idx;
    if (word === "last") idx = count - 1;
    else if (word in ORDINAL_WORDS) idx = ORDINAL_WORDS[word];
    else idx = parseInt(word, 10) - 1; // "1" / "option 2" are 1-based in speech
    return Number.isFinite(idx) && idx >= 0 && idx < count ? idx : null;
};

// One fragment as an option NAME — the same tiered rule every Otto grounding uses (exact → contains →
// token overlap), but requiring a UNIQUE winner at the best matching tier: an ambiguous name is not an
// answer. Returns the option or null.
const matchName = (frag, options) => {
    const scored = options
        .map(o => {
            const label = norm(o && o.label);
            if (!label) return { o, s: 0 };
            if (label === frag) return { o, s: 1 };
            if (frag.includes(label) || label.includes(frag)) return { o, s: 0.8 };
            const labelTokens = label.split(" ").filter(Boolean);
            const fragTokens = new Set(frag.split(" ").filter(Boolean));
            const shared = labelTokens.filter(t => fragTokens.has(t)).length;
            return { o, s: labelTokens.length && shared ? (shared / labelTokens.length) * 0.7 : 0 };
        })
        .filter(x => x.s > 0)
        .sort((a, b) => b.s - a.s);
    if (!scored.length) return null;
    for (const tier of [0.999, 0.8, 0.5]) {
        const hits = scored.filter(x => x.s >= tier);
        if (hits.length === 1) return hits[0].o;
        if (hits.length > 1) return null; // several options match equally well — ambiguous, not an answer
    }
    return null;
};

// One fragment -> an option, ordinal first ("the first one"), then name ("cfd").
const resolveFragment = (frag, options) => {
    const idx = parseOrdinal(frag, options.length);
    if (idx != null) return options[idx];
    return matchName(frag, options);
};

/**
 * Match a typed reply against a pending question's options. `multi` = the question allows several picks
 * (a value clarify) vs exactly one (a page / format / variant choice).
 */
export const matchAnswer = (text, options = [], { multi = false } = {}) => {
    const t = norm(text);
    if (!t || !Array.isArray(options) || !options.length) return null;

    if (NONE_PHRASES.has(t)) return { type: "none" };
    if (options.length === 1 && YES_PHRASES.has(t)) return { type: "pick", picks: [options[0]] };
    if (isAllPhrase(t, options.length)) {
        // "all of them" on a single-pick question is not actionable (can't open all pages) — fall through.
        if (!multi && options.length > 1) return null;
        return { type: "pick", picks: [...options], all: true };
    }

    // Whole-text first: an option label may itself contain "and" ("Regions and Sites"), so never split
    // before trying the full phrase.
    const whole = resolveFragment(t, options);
    if (whole) return { type: "pick", picks: [whole] };

    // Multi-pick: "CFD and WFD", "the first and the last", "1, 3". Every fragment must resolve uniquely —
    // a reply that is part answer, part something else is not an answer. Split the RAW text (normalization
    // strips the commas the split needs), then normalize each fragment.
    if (multi) {
        const fragments = String(text).toLowerCase().split(/,|&|\band\b/).map(norm).filter(Boolean);
        if (fragments.length > 1) {
            const picks = [];
            for (const frag of fragments) {
                const hit = resolveFragment(frag, options);
                if (!hit) return null;
                if (!picks.includes(hit)) picks.push(hit);
            }
            if (picks.length) return { type: "pick", picks };
        }
    }
    return null;
};
